import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { rememberScannerSession } from '../components/ScannerReturnPill';
import { Html5Qrcode } from 'html5-qrcode';
import {
  ArrowLeft, Camera, CheckCircle2, XCircle, AlertCircle, Users,
  Loader2, QrCode, Search, RefreshCw, List, ChevronRight, RotateCcw,
  UserPlus, Wifi, WifiOff, Mail, Trash2, Gift, Volume2, VolumeX, Copy, Ticket
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DoorListPanel({ eventId, headers }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (query) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/events/${eventId}/door-list?q=${encodeURIComponent(query)}`, { headers });
      setEntries(r.data.entries || []);
    } catch { /* no lists or no access */ }
    finally { setLoading(false); }
  };

  const checkIn = async (e) => {
    try {
      const r = await axios.post(`${API}/guest-lists/entries/${e.entry_id}/door-checkin`, {}, { headers });
      if (r.data.ok) { toast.success(r.data.message); if (r.data.note) toast.info(`⭐ ${r.data.note}`, { duration: 10000 }); }
      else toast.error(r.data.message);
      search(q);
    } catch (err) { toast.error(err.response?.data?.detail || 'Check-in failed'); }
  };

  return (
    <Card className="bg-card border-border mb-4" data-testid="door-list-panel">
      <CardContent className="p-4">
        <button className="w-full flex items-center justify-between text-sm font-semibold" onClick={() => { setOpen(o => !o); if (!open) search(''); }} data-testid="door-list-toggle">
          <span>📋 Guest Lists / Door List</span><span className="text-muted-foreground text-xs">{open ? 'hide' : 'search'}</span>
        </button>
        {open && (
          <div className="mt-3 space-y-2">
            <Input placeholder="Search name, list, or email…" value={q}
              onChange={e => { setQ(e.target.value); search(e.target.value); }} data-testid="door-list-search" />
            {loading && <p className="text-xs text-muted-foreground">Searching…</p>}
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {entries.map(e => (
                <div key={e.entry_id} className="flex items-center gap-2 text-xs py-1.5 border-b border-border/50" data-testid={`door-entry-${e.entry_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{e.name}{e.plus_ones > 0 ? ` +${e.plus_ones}` : ''}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{e.list_name}{e.note ? ` · ⭐ ${e.note}` : ''}</p>
                  </div>
                  {e.status === 'checked_in' ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[9px]">IN</Badge>
                  ) : (e.list_mode === 'ticketed' || e.ticket_codes?.length) ? (
                    <Badge variant="outline" className="text-[9px] text-indigo-300">has ticket — scan QR</Badge>
                  ) : (
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => checkIn(e)} data-testid={`door-checkin-${e.entry_id}`}>Check in</Button>
                  )}
                </div>
              ))}
              {!loading && entries.length === 0 && <p className="text-xs text-muted-foreground italic">No guest-list entries found.</p>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const SCANNER_ELEMENT_ID = 'qr-scanner-region';

export default function CheckInScanner() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [activeTab, setActiveTab] = useState('scan'); // scan, list
  const [guestSearch, setGuestSearch] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [eventOccurrences, setEventOccurrences] = useState([]);
  const [scopedOccurrenceId, setScopedOccurrenceId] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [scanLink, setScanLink] = useState(null);
  const [scanCode, setScanCode] = useState('');
  const [doorQrOpen, setDoorQrOpen] = useState(false);
  const [doorPrice, setDoorPrice] = useState('');
  const [doorQty, setDoorQty] = useState('');
  const [doorTier, setDoorTier] = useState(null);
  const [doorSaving, setDoorSaving] = useState(false);
  const loadDoorConfig = async () => {
    try {
      const r = await axios.get(`${API}/events/${eventId}/door-config`);
      setDoorTier(r.data.door_tier);
      if (r.data.door_tier) {
        setDoorPrice(String(r.data.door_tier.price));
        setDoorQty(String(r.data.door_tier.available));
      }
    } catch { /* no config yet */ }
  };
  useEffect(() => { if (doorQrOpen) loadDoorConfig(); }, [doorQrOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScanLink = async () => {
    try { const r = await axios.get(`${API}/team/events/${eventId}/scanner-link`, { headers }); if (r.data.link) setScanLink({ ...r.data.link, scanners: r.data.scanners }); else setScanLink(s => s && { ...s, scanners: r.data.scanners }); }
    catch { /* not owner */ }
  };

  const copyText = (text, msg) => {
    try { navigator.clipboard.writeText(text); toast.success(msg); }
    catch { window.prompt('Copy this link:', text); }
  };

  const createScanLink = async () => {
    try {
      const r = await axios.post(`${API}/team/events/${eventId}/scanner-link`, { passcode: scanCode }, { headers });
      setScanLink(r.data);
      copyText(r.data.join_url, 'Scanner link created & copied');
      loadScanLink();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create link'); }
  };
  const [roster, setRoster] = useState(null);
  const [resetting, setResetting] = useState(false);
  const html5QrRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  const audioCtxRef = useRef(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('scanner_sound') !== 'off');

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem('scanner_sound', next ? 'on' : 'off');
    if (next) playSound('success');
  };

  // Web Audio chimes — no asset files, works offline, instant.
  const playSound = (kind) => {
    if (localStorage.getItem('scanner_sound') === 'off') return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const t = ctx.currentTime;
      const beep = (freq, start, dur, type = 'sine', vol = 0.22) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = type;
        o.frequency.value = freq;
        g.gain.setValueAtTime(vol, t + start);
        g.gain.exponentialRampToValueAtTime(0.001, t + start + dur);
        o.connect(g);
        g.connect(ctx.destination);
        o.start(t + start);
        o.stop(t + start + dur + 0.05);
      };
      if (kind === 'success') { beep(880, 0, 0.12); beep(1318.5, 0.12, 0.2); }
      else if (kind === 'warn') { beep(740, 0, 0.14, 'triangle'); beep(740, 0.2, 0.14, 'triangle'); }
      else { beep(150, 0, 0.4, 'sawtooth', 0.3); }
    } catch { /* audio blocked — visual feedback still works */ }
  };
  const headers = { Authorization: `Bearer ${token}` };
  const QUEUE_KEY = `offline_scans_${eventId}`;
  const canManage = ['admin', 'organization'].includes(user?.role);

  useEffect(() => {
    fetchStats();
    fetchEventForOccurrences();
    const interval = setInterval(fetchStats, 10000);
    return () => {
      clearInterval(interval);
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchEventForOccurrences = async () => {
    try {
      const res = await axios.get(`${API}/hosted-events/${eventId}`, { headers });
      const ev = res.data?.event || res.data || {};
      rememberScannerSession(eventId, ev.name);
      const occs = (res.data?.occurrences || ev.occurrences || []).filter(o => o.is_active !== false);
      setEventOccurrences(occs);
    } catch { rememberScannerSession(eventId); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/checkin-stats`, { headers });
      setStats(res.data);
    } catch { /* ignore */ }
  };

  const fetchAttendees = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/ticket-sales`, { headers });
      setAttendees(res.data.attendees || []);
    } catch { toast.error('Failed to load guest list'); }
    finally { setLoadingList(false); }
  };

  useEffect(() => {
    try { setQueue(JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]')); } catch { setQueue([]); }
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (isOnline && queue.length > 0 && !syncing) syncQueue();
    // Scans queued while *online* (server hiccup / deploy blip) need a timed
    // retry — the browser 'online' event never fires for those.
    if (!isOnline || queue.length === 0) return;
    const t = setInterval(() => { if (!syncing) syncQueue(); }, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, queue.length]);

  const syncQueue = async () => {
    let pending = [];
    try { pending = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { pending = []; }
    if (pending.length === 0) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/tickets/checkin/batch`, { scans: pending }, { headers });
      localStorage.setItem(QUEUE_KEY, '[]');
      setQueue([]);
      const dup = (res.data.results || []).filter(r => r.status === 'already_checked_in').length;
      toast.success(`Synced ${res.data.synced} offline scan${res.data.synced === 1 ? '' : 's'}${dup ? ` · ${dup} already checked in` : ''}`);
      fetchStats();
      if (activeTab === 'list') fetchAttendees();
    } catch {
      toast.error('Sync failed — offline scans kept safe, try again');
    } finally { setSyncing(false); }
  };

  const queueScan = (code) => {
    const exists = queue.some(q => q.ticket_code === code);
    const next = exists ? queue : [...queue, {
      ticket_code: code,
      scanned_at: new Date().toISOString(),
      ...(scopedOccurrenceId ? { expected_occurrence_id: scopedOccurrenceId } : {}),
    }];
    setQueue(next);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(next));
    playSound('warn');
    setLastResult({ success: false, status: 'queued', message: 'Saved offline — will sync automatically', ticket: { ticket_code: code } });
    toast.warning(exists ? 'Already in offline queue' : `Saved offline (${next.length} queued)`);
  };

  const loadRoster = async () => {
    try {
      const res = await axios.get(`${API}/team/events/${eventId}/roster`, { headers });
      setRoster(res.data);
    } catch { /* non-owner */ }
  };

  const sendInvite = async () => {
    if (!inviteEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    setInviteSending(true);
    try {
      await axios.post(`${API}/team/events/${eventId}/invites`,
        { email: inviteEmail.trim(), name: inviteName.trim(), role_template: 'check_in' }, { headers });
      toast.success(`Scanner invite sent to ${inviteEmail.trim()} — they'll get an email with their door link`);
      setInviteEmail(''); setInviteName('');
      loadRoster();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invite');
    } finally { setInviteSending(false); }
  };

  const revokeInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/team/events/${eventId}/invites/${inviteId}`, { headers });
      toast.success('Invite revoked');
      loadRoster();
    } catch { toast.error('Could not revoke invite'); }
  };

  const resetAllCheckins = async () => {
    if (!window.confirm('Reset ALL check-ins for this event back to pending? Great between demos — not during a real door.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/checkin/reset`, {}, { headers });
      toast.success(`${res.data.reset_count} check-in${res.data.reset_count === 1 ? '' : 's'} reset`);
      fetchStats();
      if (activeTab === 'list') fetchAttendees();
      setLastResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setResetting(false); }
  };

  const checkinTicket = async (code) => {
    if (!code) return;
    if (!navigator.onLine) { queueScan(code); setManualCode(''); return; }
    setScanning(true);
    try {
      const payload = { ticket_code: code };
      if (scopedOccurrenceId) payload.expected_occurrence_id = scopedOccurrenceId;
      const res = await axios.post(`${API}/tickets/checkin`, payload, { headers });
      setLastResult(res.data);
      if (res.data.success) {
        playSound('success');
        toast.success(res.data.message);
        fetchStats();
        if (activeTab === 'list') fetchAttendees();
      } else if (res.data.status === 'wrong_day') {
        playSound('error');
        toast.error(res.data.message, { duration: 6000 });
      } else {
        playSound(res.data.status === 'already_checked_in' ? 'warn' : 'error');
        toast.error(res.data.message);
      }
    } catch (err) {
      const status = err.response?.status;
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response || status >= 500) {
        // Offline OR server/edge hiccup (5xx during deploys or load spikes):
        // keep the line moving — queue the scan and auto-sync when healthy.
        queueScan(code);
      } else if (status === 401) {
        playSound('error');
        toast.error('Your session expired — log in again to keep scanning', { duration: 8000 });
        setLastResult({ success: false, status: 'error', message: 'Session expired — log in again' });
      } else if (status === 403) {
        playSound('error');
        const detail = err.response?.data?.detail || "You don't have check-in access for this event";
        toast.error(detail, { duration: 8000 });
        setLastResult({ success: false, status: 'error', message: detail });
      } else {
        playSound('error');
        const detail = err.response?.data?.detail || 'Check-in failed — try again';
        toast.error(detail);
        setLastResult({ success: false, status: 'error', message: detail });
      }
    } finally {
      setScanning(false);
      setManualCode('');
    }
  };

  const undoCheckin = async (code) => {
    if (!code) return;
    setScanning(true);
    try {
      const res = await axios.post(`${API}/tickets/undo-checkin`, { ticket_code: code }, { headers });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchStats();
        fetchAttendees();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Undo failed');
    } finally {
      setScanning(false);
    }
  };

  const startCamera = async () => {
    if (cameraStarting || cameraActive) return;
    setCameraStarting(true);
    try {
      // Make scanner element exist (rendered conditionally below — wait a tick)
      setCameraActive(true);
      await new Promise(r => setTimeout(r, 100));

      const html5Qr = new Html5Qrcode(SCANNER_ELEMENT_ID, /* verbose= */ false);
      html5QrRef.current = html5Qr;
      const onSuccess = (decodedText) => {
        const match = (decodedText || '').match(/EVT-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
        const code = match ? match[0].toUpperCase() : (decodedText || '').trim().toUpperCase();
        if (!code) return;
        // Continuous scanning: camera stays open — just debounce so the same
        // QR held in frame doesn't re-fire, then move straight to the next guest.
        const now = Date.now();
        const last = lastScanRef.current;
        if (code === last.code && now - last.at < 4000) return;
        if (now - last.at < 1200) return;
        lastScanRef.current = { code, at: now };
        checkinTicket(code);
      };
      const onError = () => { /* per-frame decode misses — ignore */ };

      try {
        // Prefer back camera (environment) on mobile
        await html5Qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.333 },
          onSuccess,
          onError,
        );
      } catch (err1) {
        // Fall back to first available camera (some iOS Safari setups need this)
        try {
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw err1;
          await html5Qr.start(
            cams[0].id,
            { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.333 },
            onSuccess,
            onError,
          );
        } catch (err2) {
          throw err2;
        }
      }
    } catch (err) {
      console.error('QR camera error:', err);
      const msg = (err && err.message) ? String(err.message) : '';
      const TOAST_ID = 'qr-camera-error';
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('denied') || msg.toLowerCase().includes('notallowed')) {
        toast.error('Camera permission denied. Allow access in your browser settings.', { id: TOAST_ID });
      } else if (msg.toLowerCase().includes('notfound') || msg.toLowerCase().includes('no camera')) {
        toast.error('No camera found on this device. Use manual entry or guest list.', { id: TOAST_ID });
      } else {
        toast.error('Could not start camera. Use manual entry or guest list instead.', { id: TOAST_ID });
      }
      setCameraActive(false);
      html5QrRef.current = null;
    } finally {
      setCameraStarting(false);
    }
  };

  const stopCamera = async () => {
    const inst = html5QrRef.current;
    html5QrRef.current = null;
    setCameraActive(false);
    if (inst) {
      try {
        if (typeof inst.isScanning !== 'undefined' ? inst.isScanning : true) {
          await inst.stop();
        }
        await inst.clear();
      } catch { /* already stopped */ }
    }
  };

  // Filter attendees by search + occurrence scope
  const txt = (v) => String(v ?? '').toLowerCase();
  const filteredAttendees = attendees.filter(a => {
    if (scopedOccurrenceId && a.occurrence_id !== scopedOccurrenceId) return false;
    if (!guestSearch) return true;
    const q = guestSearch.toLowerCase();
    return (txt(a.name).includes(q) || txt(a.email).includes(q) || txt(a.phone).includes(q) || txt(a.code).includes(q));
  });

  // Quick lookup for occurrence label/date by id
  const occLookup = {};
  eventOccurrences.forEach(o => { occLookup[o.occurrence_id] = o; });

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background pt-16 pb-8">
      <div className="max-w-lg mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/events')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-[130px]">
            <h1 className="font-heading text-lg sm:text-xl font-bold text-foreground whitespace-nowrap">Check-In</h1>
            <p className="text-[11px] text-muted-foreground whitespace-nowrap">Scan or search guests</p>
          </div>
          <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={fetchStats}><RefreshCw className="h-3 w-3" /></Button>
          <Button size="sm" variant="outline" onClick={toggleSound} data-testid="sound-toggle-btn" title={soundOn ? 'Mute scan sounds' : 'Unmute scan sounds'} className={soundOn ? '' : 'text-muted-foreground'}>
            {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
          </Button>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => { setInviteOpen(true); loadRoster(); loadScanLink(); }} data-testid="invite-scanner-btn" title="Invite a scanner">
              <UserPlus className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setDoorQrOpen(true)} data-testid="door-sale-btn" title="Sell a ticket at the door" className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10">
            <Ticket className="h-3.5 w-3.5" />
          </Button>
          {canManage && (
            <Button size="sm" variant="outline" onClick={resetAllCheckins} disabled={resetting} data-testid="reset-checkins-btn" title="Reset all check-ins" className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10">
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            </Button>
          )}
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="checkin-stats">
            <Card className="bg-emerald-500/5 border-emerald-500/20">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">{stats.checked_in}</p>
                <p className="text-[10px] text-muted-foreground">Checked In</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-500/5 border-amber-500/20">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
                <p className="text-[10px] text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-indigo-500/5 border-indigo-500/20">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-foreground">{stats.percentage}%</p>
                <p className="text-[10px] text-muted-foreground">Arrival Rate</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Offline mode banner */}
        {(!isOnline || queue.length > 0) && (
          <Card className={`mb-4 ${!isOnline ? 'border-amber-500/40 bg-amber-500/10' : 'border-sky-500/40 bg-sky-500/10'}`} data-testid="offline-banner">
            <CardContent className="p-3 flex items-center gap-3">
              {!isOnline ? <WifiOff className="h-4 w-4 text-amber-400 flex-shrink-0" /> : <Wifi className="h-4 w-4 text-sky-400 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">{!isOnline ? 'Offline mode — keep scanning' : 'Back online'}</p>
                <p className="text-[10px] text-muted-foreground" data-testid="offline-queue-count">
                  {queue.length} scan{queue.length === 1 ? '' : 's'} queued{!isOnline ? ' · will sync automatically when connection returns' : ''}
                </p>
              </div>
              {isOnline && queue.length > 0 && (
                <Button type="button" size="sm" className="h-7 text-xs bg-sky-600 hover:bg-sky-700" onClick={syncQueue} disabled={syncing} data-testid="sync-now-btn">
                  {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Sync now'}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Multi-day occurrence scope picker */}
        {eventOccurrences.length > 0 && (
          <Card className="border-amber-500/30 bg-amber-500/5 mb-4" data-testid="occurrence-scope">
            <CardContent className="p-3 space-y-2">
              <p className="text-[10px] font-semibold text-amber-300 uppercase tracking-wide">Scoped to day</p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setScopedOccurrenceId('')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${!scopedOccurrenceId ? 'border-amber-500 bg-amber-500/20 text-amber-300' : 'border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid="scope-all-days"
                >
                  All days
                </button>
                {eventOccurrences.map(occ => {
                  const occStats = stats?.by_occurrence?.[occ.occurrence_id];
                  return (
                    <button
                      key={occ.occurrence_id}
                      onClick={() => setScopedOccurrenceId(occ.occurrence_id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition flex items-center gap-1.5 ${scopedOccurrenceId === occ.occurrence_id ? 'border-amber-500 bg-amber-500/20 text-amber-300' : 'border-border text-muted-foreground hover:text-foreground'}`}
                      data-testid={`scope-${occ.occurrence_id}`}
                    >
                      <span>{occ.label || occ.date}</span>
                      {occStats && (
                        <span className="text-[10px] opacity-70">({occStats.checked_in}/{occStats.total})</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground">
                {scopedOccurrenceId ? 'Scanner will reject tickets for other days.' : 'Scanner will accept any valid ticket regardless of day.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tab Switcher */}
        <div className="flex gap-1 mb-4 bg-muted/40 rounded-lg p-1" data-testid="checkin-tabs">
          <button className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'scan' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => setActiveTab('scan')}>
            <QrCode className="h-4 w-4" /> QR / Code
          </button>
          <button className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'list' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => { setActiveTab('list'); if (attendees.length === 0) fetchAttendees(); }}>
            <List className="h-4 w-4" /> Guest List
          </button>
        </div>

        {/* ===== QR / Code Tab ===== */}
        {activeTab === 'scan' && (
          <>
            {/* Camera Scanner */}
            <Card className="bg-card border-border mb-4">
              <CardContent className="p-4">
                {cameraActive ? (
                  <div className="space-y-3">
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <div id={SCANNER_ELEMENT_ID} style={{ width: '100%', minHeight: '280px' }} data-testid="qr-scanner-region" />
                      {lastResult && (
                        <div
                          className={`absolute bottom-0 left-0 right-0 px-3 py-2 flex items-center gap-2 backdrop-blur-sm ${lastResult.success ? 'bg-emerald-600/90' : lastResult.status === 'queued' ? 'bg-sky-600/90' : lastResult.status === 'already_checked_in' ? 'bg-amber-600/90' : 'bg-red-600/90'}`}
                          data-testid="camera-result-strip"
                        >
                          {lastResult.success ? <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" /> : lastResult.status === 'queued' ? <WifiOff className="h-5 w-5 text-white flex-shrink-0" /> : lastResult.status === 'already_checked_in' ? <AlertCircle className="h-5 w-5 text-white flex-shrink-0" /> : <XCircle className="h-5 w-5 text-white flex-shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-white truncate">
                              {lastResult.success ? lastResult.ticket?.buyer_name : lastResult.status === 'already_checked_in' ? `Already in — ${lastResult.ticket?.buyer_name || ''}` : lastResult.status === 'queued' ? 'Saved offline' : lastResult.message}
                            </p>
                            {lastResult.ticket?.kickback_perk && (
                              <p className="text-[11px] font-bold text-amber-300 truncate">👑 PERK: {lastResult.ticket.kickback_perk}</p>
                            )}
                            {(lastResult.ticket?.addons || []).length > 0 && (
                              <p className="text-[11px] text-white/90 truncate">
                                <Gift className="h-3 w-3 inline mr-1" />
                                {lastResult.ticket.addons.map(ad => `${ad.quantity}× ${ad.name}`).join(' · ')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-center text-muted-foreground">Continuous mode — keep scanning, one guest after another</p>
                    <div className="flex gap-2">
                      <Button type="button" className="flex-1" variant="outline" onClick={stopCamera} data-testid="stop-camera">
                        <XCircle className="h-4 w-4 mr-2" /> Stop Camera
                      </Button>
                      <Button type="button" variant="outline" onClick={async () => { await stopCamera(); setTimeout(startCamera, 400); }} data-testid="restart-camera" title="Camera frozen or black? Restart it">
                        <RefreshCw className="h-4 w-4 mr-2" /> Restart
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button type="button" className="w-full h-20 bg-indigo-600 hover:bg-indigo-700 text-lg" onClick={startCamera} disabled={cameraStarting} data-testid="start-camera">
                    {cameraStarting ? <Loader2 className="h-6 w-6 mr-3 animate-spin" /> : <Camera className="h-6 w-6 mr-3" />}
                    {cameraStarting ? 'Starting camera…' : 'Scan QR Code'}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Manual Entry */}
            <Card className="bg-card border-border mb-4">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-2">Or enter ticket code manually:</p>
                <div className="flex gap-2">
                  <Input
                    value={manualCode}
                    onChange={e => setManualCode(e.target.value.toUpperCase())}
                    onKeyDown={e => { if (e.key === 'Enter') checkinTicket(manualCode); }}
                    placeholder="EVT-XXXX-XXXX"
                    className="font-mono text-center tracking-wider"
                    data-testid="manual-code-input"
                  />
                  <Button type="button" onClick={() => checkinTicket(manualCode)} disabled={scanning || !manualCode} className="bg-indigo-600 hover:bg-indigo-700" data-testid="manual-checkin-btn">
                    {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ===== Guest List Tab ===== */}
        {activeTab === 'list' && (
          <>
            <Card className="bg-card border-border mb-4">
              <CardContent className="p-4">
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={guestSearch}
                    onChange={e => setGuestSearch(e.target.value)}
                    placeholder="Search by name, email, or phone..."
                    className="pl-10"
                    data-testid="guest-search"
                  />
                </div>
                {loadingList ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                ) : filteredAttendees.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    {guestSearch ? 'No guests match your search' : 'No tickets sold yet'}
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-[60vh] overflow-y-auto" data-testid="guest-list">
                    {filteredAttendees.map((a, i) => {
                      const isCheckedIn = a.status === 'checked_in';
                      const isCancelled = a.status === 'cancelled';
                      return (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${isCancelled ? 'bg-red-500/5 border-red-500/20 opacity-60' : isCheckedIn ? 'bg-emerald-500/5 border-emerald-500/20' : 'border-border hover:border-indigo-500/30'}`} data-testid={`guest-${a.code}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-foreground truncate">{a.name}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{a.email}{a.phone ? ` / ${a.phone}` : ''}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <Badge variant="outline" className="text-[9px]">{a.type}</Badge>
                              {a.source === 'eventbrite_import' && (
                                <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/30 text-[9px]" data-testid={`guest-source-${a.code}`}>
                                  Eventbrite
                                </Badge>
                              )}
                              {(a.addons || []).map((ad, j) => (
                                <Badge key={j} className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 text-[9px]" data-testid={`guest-addon-${a.code}-${j}`}>
                                  <Gift className="h-2.5 w-2.5 mr-0.5" /> {ad.quantity}&times; {ad.name}{ad.variation_name ? ` (${ad.variation_name})` : ''}
                                </Badge>
                              ))}
                              {a.occurrence_id && occLookup[a.occurrence_id] && (
                                <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[9px]" data-testid={`guest-day-badge-${a.code}`}>
                                  {occLookup[a.occurrence_id].label || occLookup[a.occurrence_id].date}
                                </Badge>
                              )}
                              <span className="text-[10px] font-mono text-muted-foreground">{a.code}</span>
                            </div>
                          </div>
                          {isCancelled ? (
                            <Badge className="bg-red-500/20 text-red-400 text-xs flex-shrink-0">
                              <XCircle className="h-3 w-3 mr-1" /> Cancelled
                            </Badge>
                          ) : isCheckedIn ? (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> In
                              </Badge>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-xs h-7 px-2 text-muted-foreground hover:text-amber-400"
                                onClick={() => undoCheckin(a.code)}
                                disabled={scanning}
                                data-testid={`undo-btn-${a.code}`}
                              >
                                <RotateCcw className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700 text-xs h-8 flex-shrink-0"
                              onClick={() => checkinTicket(a.code)}
                              disabled={scanning}
                              data-testid={`checkin-btn-${a.code}`}
                            >
                              {scanning ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Check In</>}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex justify-between items-center mt-3 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">{filteredAttendees.length} of {attendees.length} guests</span>
                  <Button type="button" size="sm" variant="outline" className="text-xs h-7" onClick={fetchAttendees}><RefreshCw className="h-3 w-3 mr-1" /> Refresh</Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Last Scan Result */}
        {lastResult && (
          <Card className={`border-2 mb-4 ${lastResult.success ? 'border-emerald-500 bg-emerald-500/5' : lastResult.status === 'queued' ? 'border-sky-500 bg-sky-500/5' : lastResult.status === 'already_checked_in' ? 'border-amber-500 bg-amber-500/5' : lastResult.status === 'wrong_day' ? 'border-orange-500 bg-orange-500/5' : 'border-red-500 bg-red-500/5'}`} data-testid="scan-result">
            <CardContent className="p-4 text-center">
              {lastResult.success ? (
                <>
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
                  <p className="text-lg font-bold text-emerald-400">Checked In!</p>
                  <p className="text-sm text-foreground">{lastResult.ticket?.buyer_name}</p>
                  <p className="text-xs text-muted-foreground">{lastResult.ticket?.ticket_type_name} &middot; {lastResult.ticket?.ticket_code}</p>
                  {lastResult.ticket?.source === 'eventbrite_import' && (
                    <Badge className="mt-1 bg-orange-500/15 text-orange-300 border-orange-500/30 text-[10px]" data-testid="result-source-badge">
                      Imported · Eventbrite
                    </Badge>
                  )}
                  {lastResult.party_remaining > 0 && (
                    <Button
                      type="button" size="sm"
                      className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={scanning}
                      onClick={async () => {
                        try {
                          const r = await axios.post(`${API}/tickets/checkin-order`,
                            { order_id: lastResult.ticket.order_id }, { headers });
                          playSound('success');
                          toast.success(`Party checked in — ${r.data.checked_in} more ticket${r.data.checked_in === 1 ? '' : 's'}`);
                          setLastResult(lr => ({ ...lr, party_remaining: 0 }));
                          fetchStats();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Bulk check-in failed'); }
                      }}
                      data-testid="checkin-party-btn"
                    >
                      <Users className="h-4 w-4 mr-1.5" />
                      Check in whole party ({lastResult.party_remaining} more)
                    </Button>
                  )}
                  {lastResult.ticket?.guest_list_name && (
                    <div className="mt-2 space-y-1" data-testid="result-guest-list">
                      <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/40 text-[11px] font-bold">
                        📋 {lastResult.ticket.guest_list_name} LIST
                      </Badge>
                      {lastResult.ticket?.guest_list_note && (
                        <p className="text-xs font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1.5" data-testid="result-guest-list-note">
                          ⭐ {lastResult.ticket.guest_list_note}
                        </p>
                      )}
                    </div>
                  )}
                  {lastResult.ticket?.kickback_perk && (
                    <Badge className="mt-2 bg-amber-500/20 text-amber-300 border-amber-500/40 text-[11px] font-bold" data-testid="result-kickback-perk">
                      👑 PERK UNLOCKED: {lastResult.ticket.kickback_perk}
                    </Badge>
                  )}
                  {(lastResult.ticket?.addons || []).length > 0 && (
                    <div className="flex flex-wrap justify-center gap-1.5 mt-2" data-testid="result-addons">
                      {lastResult.ticket.addons.map((ad, j) => (
                        <Badge key={j} className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 text-[10px]">
                          <Gift className="h-3 w-3 mr-1" /> {ad.quantity}&times; {ad.name}{ad.variation_name ? ` (${ad.variation_name})` : ''}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {lastResult.ticket?.occurrence_label && (
                    <Badge className="mt-2 bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[10px]" data-testid="result-day-badge">
                      {lastResult.ticket.occurrence_date} · {lastResult.ticket.occurrence_label}
                    </Badge>
                  )}
                </>
              ) : lastResult.status === 'queued' ? (
                <>
                  <WifiOff className="h-12 w-12 text-sky-400 mx-auto mb-2" />
                  <p className="text-lg font-bold text-sky-400">Saved Offline</p>
                  <p className="text-sm text-foreground font-mono">{lastResult.ticket?.ticket_code}</p>
                  <p className="text-xs text-muted-foreground">Will sync automatically when back online</p>
                </>
              ) : lastResult.status === 'already_checked_in' ? (
                <>
                  <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-2" />
                  <p className="text-lg font-bold text-amber-400">Already Checked In</p>
                  <p className="text-sm text-foreground">{lastResult.ticket?.buyer_name}</p>
                  <p className="text-xs text-muted-foreground">Checked in at {new Date(lastResult.ticket?.checked_in_at).toLocaleTimeString()}</p>
                  {(lastResult.ticket?.addons || []).length > 0 && (
                    <div className="flex flex-wrap justify-center gap-1.5 mt-2">
                      {lastResult.ticket.addons.map((ad, j) => (
                        <Badge key={j} className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 text-[10px]">
                          <Gift className="h-3 w-3 mr-1" /> {ad.quantity}&times; {ad.name}{ad.variation_name ? ` (${ad.variation_name})` : ''}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {lastResult.ticket?.occurrence_label && (
                    <Badge className="mt-2 bg-indigo-500/15 text-indigo-300 border-indigo-500/30 text-[10px]">
                      {lastResult.ticket.occurrence_date} · {lastResult.ticket.occurrence_label}
                    </Badge>
                  )}
                </>
              ) : lastResult.status === 'wrong_day' ? (
                <>
                  <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-2" />
                  <p className="text-lg font-bold text-orange-400">Wrong Day</p>
                  <p className="text-sm text-foreground">{lastResult.ticket?.buyer_name}</p>
                  <p className="text-xs text-muted-foreground">{lastResult.message}</p>
                  {lastResult.ticket?.occurrence_label && (
                    <Badge className="mt-2 bg-orange-500/15 text-orange-300 border-orange-500/30 text-[10px]">
                      Ticket for: {lastResult.ticket.occurrence_date} · {lastResult.ticket.occurrence_label}
                    </Badge>
                  )}
                </>
              ) : (
                <>
                  <XCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
                  <p className="text-lg font-bold text-red-400">{lastResult.status === 'cancelled' ? 'Cancelled Ticket' : 'Invalid Ticket'}</p>
                  <p className="text-sm text-muted-foreground">{lastResult.message}</p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Guest / Door List search */}
        <DoorListPanel eventId={eventId} headers={headers} />

        {/* By Type Breakdown */}
        {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
          <Card className="bg-card border-border mb-4">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-2">By Ticket Type</h3>
              <div className="space-y-2">
                {Object.entries(stats.by_type).map(([type, data]) => (
                  <div key={type} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-28 truncate">{type}</span>
                    <div className="flex-1 h-2 bg-muted/40 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${data.total ? (data.checked_in / data.total * 100) : 0}%` }} />
                    </div>
                    <span className="text-xs font-medium w-12 text-right">{data.checked_in}/{data.total}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Check-ins */}
        {stats?.recent_checkins?.length > 0 && (
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold mb-2">Recent Check-ins</h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {stats.recent_checkins.map((ci, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-border/50">
                    <div>
                      <span className="font-medium text-foreground">{ci.name}</span>
                      <Badge variant="outline" className="text-[9px] ml-1.5">{ci.type}</Badge>
                    </div>
                    <span className="text-muted-foreground">{new Date(ci.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Door ticket sale QR */}
        <Dialog open={doorQrOpen} onOpenChange={setDoorQrOpen}>
          <DialogContent className="max-w-sm text-center max-h-[90vh] overflow-y-auto" data-testid="door-sale-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 justify-center"><Ticket className="h-4 w-4 text-emerald-400" /> Door ticket sale</DialogTitle>
              <DialogDescription>Show this QR to the guest — it opens a one-tap door checkout on their phone.</DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-border p-3 space-y-2 text-left" data-testid="door-config">
              <p className="text-xs font-semibold text-foreground">Door ticket price & how many are left</p>
              <div className="flex gap-2 items-center">
                <Input type="number" min="0" step="1" placeholder="Price $" value={doorPrice}
                  onChange={e => setDoorPrice(e.target.value)} className="h-8 text-sm" data-testid="door-price-input" />
                <Input type="number" min="0" step="1" placeholder="Qty" value={doorQty}
                  onChange={e => setDoorQty(e.target.value)} className="h-8 text-sm w-20" data-testid="door-qty-input" />
                <Button type="button" size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={doorSaving}
                  onClick={async () => {
                    setDoorSaving(true);
                    try {
                      await axios.post(`${API}/events/${eventId}/door-config`,
                        { price: parseFloat(doorPrice || 0), quantity: parseInt(doorQty || 0, 10), active: true }, { headers });
                      toast.success('Door ticket is live');
                      loadDoorConfig();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
                    finally { setDoorSaving(false); }
                  }} data-testid="door-config-save">
                  {doorSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                </Button>
              </div>
              {doorTier ? (
                <p className="text-[11px] text-emerald-400">✓ Live: ${Number(doorTier.price).toFixed(2)} · {doorTier.available} left at the door</p>
              ) : (
                <p className="text-[11px] text-amber-400">Set a price & quantity first — the QR checkout needs it.</p>
              )}
            </div>
            <div className="bg-white rounded-xl p-5 inline-block mx-auto">
              <QRCodeSVG value={`${window.location.origin}/door/${eventId}`} size={220} data-testid="door-sale-qr" />
            </div>
            <p className="text-xs text-muted-foreground">They pay on their phone and their QR ticket pops up instantly — scan it seconds later.</p>
          </DialogContent>
        </Dialog>

        {/* Invite Scanner dialog */}
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent className="max-w-md" data-testid="invite-scanner-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Invite a Scanner</DialogTitle>
              <DialogDescription>
                They'll get an email with a link to accept — then they can scan tickets and check in guests for this event only. Access expires after the event.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {/* Quick link + 4-digit passcode (fastest day-of) */}
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="scanner-quick-link">
                <p className="text-xs font-semibold text-emerald-400">⚡ Quick link + passcode (fastest)</p>
                {scanLink ? (
                  <>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => copyText(scanLink.join_url, 'Scanner link copied — text it to your helper')} data-testid="copy-scanner-link">
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy link
                      </Button>
                      <span className="font-mono text-lg tracking-[0.3em] font-bold text-emerald-300 px-2 self-center" data-testid="scanner-passcode">{scanLink.passcode}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">They open the link, enter the 4-digit code + their name, email & phone — and they're scanning. Access ends after the event.</p>
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Input inputMode="numeric" maxLength={4} className="w-24 text-center font-mono" placeholder="4 digits" value={scanCode} onChange={e => setScanCode(e.target.value.replace(/\D/g, '').slice(0, 4))} data-testid="scanner-passcode-input" />
                    <Button type="button" size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={createScanLink} disabled={scanCode.length !== 4} data-testid="create-scanner-link">Create scanner link</Button>
                  </div>
                )}
                {(scanLink?.scanners || []).length > 0 && (
                  <div className="pt-1 space-y-1">
                    {(scanLink.scanners || []).map(s => (
                      <div key={s.user_id} className="flex justify-between text-[11px]">
                        <span className="truncate">{s.name || s.email}{s.phone ? ` · ${s.phone}` : ''}</span>
                        <span className="text-emerald-400 font-semibold">{(s.checkins || 0) + (s.door_checkins || 0)} checked in</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Or send an email invite</p>
              <Input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="scanner@example.com"
                data-testid="invite-scanner-email"
              />
              <Input
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
                placeholder="Name (optional)"
                data-testid="invite-scanner-name"
              />
              <Button type="button" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={sendInvite} disabled={inviteSending || !inviteEmail} data-testid="invite-scanner-send">
                {inviteSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Send Scanner Invite
              </Button>

              {roster && ((roster.members || []).length > 0 || (roster.invites || []).length > 0) && (
                <div className="pt-2 border-t border-border space-y-1.5 max-h-48 overflow-y-auto">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Event team</p>
                  {(roster.members || []).map(m => (
                    <div key={m.member_id} className="flex items-center justify-between text-xs py-1" data-testid={`roster-member-${m.member_id}`}>
                      <div className="min-w-0">
                        <span className="font-medium text-foreground">{m.name || m.email}</span>
                        <Badge variant="outline" className="text-[9px] ml-1.5">{(roster.role_labels || {})[m.role_template] || m.role_template}</Badge>
                      </div>
                      <Badge className="bg-emerald-500/15 text-emerald-400 text-[9px]">Active</Badge>
                    </div>
                  ))}
                  {(roster.invites || []).map(inv => (
                    <div key={inv.invite_id} className="flex items-center justify-between text-xs py-1" data-testid={`roster-invite-${inv.invite_id}`}>
                      <div className="min-w-0">
                        <span className="text-foreground truncate">{inv.email}</span>
                        <Badge variant="outline" className="text-[9px] ml-1.5">{(roster.role_labels || {})[inv.role_template] || inv.role_template}</Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge className="bg-amber-500/15 text-amber-400 text-[9px]">Pending</Badge>
                        <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400" onClick={() => revokeInvite(inv.invite_id)} data-testid={`revoke-invite-${inv.invite_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                Need co-organizers or full managers? Use the Team tab on the event dashboard.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
